import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/apiConfig';
import { socket } from '../config/socket';
import './Mesas.css';

const MesasModule = () => {
  const [mesas, setMesas] = useState([]);
  const [pisoActual, setPisoActual] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mesaSeleccionada, setMesaSeleccionada] = useState(null);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [meseros, setMeseros] = useState([]);
  const [loadingMeseros, setLoadingMeseros] = useState(false);
  const [mensajeActualizacion, setMensajeActualizacion] = useState('');
  const [mesasConProductosListos, setMesasConProductosListos] = useState([]);
  const { userInfo, auth } = useAuth();
  const navigate = useNavigate();

  // Piso asignado al usuario obtenido en tiempo real desde la DB (evita JWT desactualizado)
  // null = sin restricción, 0 = solo pedidos directos, 1+ = piso específico
  const [pisoAsignadoDB, setPisoAsignadoDB] = useState(
    ['MOZO', 'CAJERO'].includes(auth?.user?.rol) ? (auth?.user?.piso_asignado ?? null) : null
  );
  const pisoRestringido = ['MOZO', 'CAJERO'].includes(auth?.user?.rol) ? pisoAsignadoDB : null;

  // Ref para que fetchMesas pueda leer pisoRestringido sin agregarlo como dependencia
  // (evita cascade de re-fetches cuando pisoRestringido cambia)
  const pisoRestringidoRef = useRef(null);
  useEffect(() => {
    pisoRestringidoRef.current = pisoRestringido;
  }, [pisoRestringido]);

  // Función auxiliar para obtener el token de autenticación - Mejorada
  const getToken = useCallback(() => {
    const authData = localStorage.getItem('auth');
    if (!authData) return null;
    try {
      const auth = JSON.parse(authData);
      return auth.token;
    } catch (e) {
      console.error('Error al obtener el token:', e);
      return null;
    }
  }, []);

  // Fetch real-time de asignaciones para evitar JWT desactualizado
  useEffect(() => {
    if (!auth?.token || !['MOZO', 'CAJERO'].includes(auth?.user?.rol)) return;
    fetch(getApiUrl('/usuarios-admin/mis-asignaciones'), {
      headers: { 'Authorization': `Bearer ${auth.token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPisoAsignadoDB(data.piso_asignado ?? null); })
      .catch(() => {});
  }, [auth?.token, auth?.user?.rol]);

  // Versión súper-optimizada puramente local sincronizada con el estado del backend API /mesas sin el antipatrón de red N+1
  const verificarProductosListos = useCallback(() => {
    try {
      // Filtrar estúpidamente rápido basado en la nueva optimización local
      const mesasListos = mesas
        .filter(mesa => mesa.tiene_productos_listos === true)
        .map(mesa => mesa.id);
      
      // Actualizar el estado solo si hay cambios
      setMesasConProductosListos(prevState => {
        if (JSON.stringify(prevState) !== JSON.stringify(mesasListos)) {
          return mesasListos;
        }
        return prevState;
      });
    } catch (err) {
      console.error('Error en verificarProductosListos de manera local:', err);
    }
  }, [mesas]);

  // Función mejorada para cargar las mesas
  const fetchMesas = useCallback(async () => {
    try {
      setLoading(true);
      
      const token = getToken();
      
      if (!token) {
        setError('No hay sesión activa. Por favor, inicia sesión nuevamente.');
        setLoading(false);
        return;
      }
      
      const response = await fetch(getApiUrl('/mesas'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error del servidor: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Filtrar para excluir la mesa número 0
      const mesasFiltradas = data.filter(mesa => mesa.numero !== 0);
      setMesas(mesasFiltradas);
      
      // Auto-cambiar piso solo si el usuario NO tiene piso asignado
      // (si tiene piso asignado, pisoRestringidoRef.current !== null → no interferir)
      if (mesasFiltradas.length > 0 && (pisoRestringidoRef.current === null || pisoRestringidoRef.current === undefined)) {
        // Normalizar a número para evitar mismatch string/número desde la DB
        const pisosDisponibles = [...new Set(mesasFiltradas.map(mesa => Number(mesa.piso)))]
          .filter(p => p > 0)
          .sort((a, b) => a - b);
        if (!pisosDisponibles.includes(Number(pisoActual)) && pisosDisponibles.length > 0) {
          setPisoActual(pisosDisponibles[0]);
        }
      }
    } catch (err) {
      console.error('Error en fetchMesas:', err);
      setError(err.message || 'Error desconocido al cargar las mesas');
    } finally {
      setLoading(false);
    }
  }, [getToken, pisoActual]);

  // Efecto para cargar datos iniciales
  useEffect(() => {
    fetchMesas();
    // Solo obtener meseros si el usuario es MOZO o ENCARGADO
    if (userInfo && (userInfo.rol === 'MOZO' || userInfo.rol === 'ENCARGADO')) {
      fetchMeseros();
    }
  }, [fetchMesas, userInfo]);

  // Si el usuario tiene piso asignado, forzar el piso inicial al asignado
  useEffect(() => {
    if (pisoRestringido !== null && pisoRestringido !== undefined && pisoRestringido > 0) {
      setPisoActual(Number(pisoRestringido));
    }
  }, [pisoRestringido]);

  // Efecto para verificar productos listos después de cargar las mesas
  useEffect(() => {
    if (mesas.length > 0) {
      verificarProductosListos();
    }
  }, [mesas, verificarProductosListos]);

  // Efecto para escuchar eventos de Socket.io en tiempo real
  useEffect(() => {
    const handleSocketUpdate = (data) => {
      console.log('Evento en tiempo real recibido:', data);
      fetchMesas();
    };

    socket.on('pedidoCreado', handleSocketUpdate);
    socket.on('mesaActualizada', handleSocketUpdate);

    // Agregar event listener para verificar también cuando la ventana vuelve a tener focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Ventana visible, recargando datos...');
        fetchMesas();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      socket.off('pedidoCreado', handleSocketUpdate);
      socket.off('mesaActualizada', handleSocketUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchMesas]);

  // Escuchar eventos personalizados para actualización inmediata
  useEffect(() => {
    const handleEstadoCambiado = (event) => {
      console.log('Evento de cambio de estado recibido:', event.detail);
      
      // Si el evento tiene información de mesa, actualizamos inmediatamente
      if (event.detail && event.detail.idMesa) {
        // Verificar si ya tenemos esta mesa marcada como con productos listos
        if (event.detail.estado === 'listo') {
          setMesasConProductosListos(prev => {
            if (!prev.includes(event.detail.idMesa)) {
              console.log(`Agregando mesa ${event.detail.idMesa} a productos listos`);
              return [...prev, event.detail.idMesa];
            }
            return prev;
          });
        } else if (
          // Si el estado es algo diferente a "listo", verificamos si debemos quitar la mesa
          event.detail.todosLosProductosNoEstanListos
        ) {
          setMesasConProductosListos(prev => 
            prev.filter(id => id !== event.detail.idMesa)
          );
        }
      } else {
        // Si no tenemos información específica, recargamos todos los datos
        fetchMesas();
        verificarProductosListos();
      }
    };
    
    document.addEventListener('productoEstadoCambiado', handleEstadoCambiado);
    
    return () => {
      document.removeEventListener('productoEstadoCambiado', handleEstadoCambiado);
    };
  }, [fetchMesas, verificarProductosListos]);

  const fetchMeseros = async () => {
    try {
      setLoadingMeseros(true);
      
      const token = getToken();
      
      if (!token) {
        console.error('No hay token disponible');
        return;
      }
      
      const response = await fetch(getApiUrl('/usuarios?rol=MOZO'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener la lista de meseros');
      }
      
      const data = await response.json();
      setMeseros(data);
    } catch (err) {
      console.error('Error en fetchMeseros:', err);
    } finally {
      setLoadingMeseros(false);
    }
  };

  const actualizarEstadoMesa = async (id, nuevoEstado) => {
    try {
      const token = getToken();
      
      if (!token) {
        setError('No hay sesión activa. Por favor, inicia sesión nuevamente.');
        return;
      }
      
      setMensajeActualizacion('Actualizando estado...');
      
      const response = await fetch(getApiUrl(`/mesas/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          estado: nuevoEstado, 
          id_mesero: mesaSeleccionada?.id_mesero || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al actualizar el estado de la mesa');
      }
      
      const mesaActualizada = await response.json();
      
      // Actualizar la lista completa de mesas para mantener consistencia
      await fetchMesas();
      
      setMensajeActualizacion(`¡Mesa cambiada a estado: ${nuevoEstado}!`);
      
      setTimeout(() => {
        setMensajeActualizacion('');
      }, 3000);
      
      if (nuevoEstado === 'libre' && mesaSeleccionada?.id === id) {
        setMostrarDetalle(false);
        setMesaSeleccionada(null);
      } else if (mesaSeleccionada?.id === id) {
        setMesaSeleccionada(mesaActualizada);
      }
      
      // Si la mesa se marca como libre, remover de la lista de mesas con productos listos
      if (nuevoEstado === 'libre') {
        setMesasConProductosListos(prev => prev.filter(mesaId => mesaId !== id));
      }
    } catch (err) {
      setError(err.message);
      setMensajeActualizacion('');
    }
  };

  const asignarMesero = async (idMesa, idMesero) => {
    try {
      const token = getToken();
      
      if (!token) {
        setError('No hay sesión activa. Por favor, inicia sesión nuevamente.');
        return;
      }
      
      setMensajeActualizacion('Asignando mesero...');
      
      const response = await fetch(getApiUrl(`/mesas/${idMesa}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          estado: mesaSeleccionada.estado, 
          id_mesero: idMesero || null // Asegurarse de enviar null si no hay mesero
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al asignar mesero');
      }
      
      // Actualizar estado local con la respuesta del servidor
      const mesaActualizada = await response.json();
      
      // Actualizar la lista completa de mesas para mantener consistencia
      await fetchMesas();
      
      // Buscar el nombre del mesero para mostrar en el mensaje
      const meseroAsignado = meseros.find(m => m.id === Number(idMesero));
      const nombreMesero = idMesero && meseroAsignado 
        ? `${meseroAsignado.nombre} ${meseroAsignado.apellido || ''}` 
        : 'Sin mesero';
      
      // Actualizar la mesa seleccionada
      if (mesaSeleccionada && mesaSeleccionada.id === idMesa) {
        setMesaSeleccionada(mesaActualizada);
      }
      
      setMensajeActualizacion(`¡Mesero asignado: ${nombreMesero}!`);
      
      setTimeout(() => {
        setMensajeActualizacion('');
      }, 3000);
    } catch (err) {
      setError(err.message);
      setMensajeActualizacion('');
    }
  };

  const handleMesaClick = (mesa) => {
    console.log('Mesa seleccionada:', mesa);
    navigate(`/mesa/${mesa.id}/pedidos`);
  };

  const getColorEstado = (mesa) => {
    // Verificar si la mesa tiene productos listos para recoger
    const tieneProductosListos = mesasConProductosListos.includes(mesa.id);
    
    if (tieneProductosListos) {
      return 'mesa-con-productos-listos'; // Clase especial para mesas con productos listos
    }
    
    // Si no tiene productos listos, devolver la clase basada en el estado normal
    switch(mesa.estado) {
      case 'libre': return 'mesa-libre';
      case 'ocupada': return 'mesa-ocupada';
      case 'pendiente_pago': return 'mesa-pendiente';
      default: return '';
    }
  };

  const filtrarMesasPorPiso = (piso) => {
    // Filtrar por piso y asegurarse de excluir la mesa número 0
    // Normalizar a número para evitar mismatch string/número (pg retorna numeric como string)
    return mesas.filter(mesa => Number(mesa.piso) === Number(piso) && mesa.numero !== 0);
  };

  const navigateToPedidos = (idMesa, accion) => {
    if (accion === 'nuevo') {
      navigate(`/pedidos/nuevo/${idMesa}`);
    } else {
      navigate(`/pedidos/mesa/${idMesa}`);
    }
  };

  // if (loading) {
  //   return <div className="loading">Cargando mesas...</div>;
  // }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  const mesasFiltradas = filtrarMesasPorPiso(pisoActual);
  const pisosDisponibles = [...new Set(mesas.map(mesa => Number(mesa.piso)))].filter(p => p > 0).sort((a, b) => a - b);

  return (
    <div className="mesas-container">
      {/* Usuario restringido a piso 0: solo pedidos directos, sin mesas */}
      {pisoRestringido === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🍽️</div>
          <p style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>Solo pedidos directos</p>
          <p style={{ fontSize: '0.9rem' }}>Tu cuenta está configurada para atender únicamente pedidos directos de barra.</p>
          <button
            onClick={() => navigate('/pedido-directo')}
            style={{
              marginTop: '1rem', padding: '0.6rem 1.5rem', borderRadius: 8, border: 'none',
              background: '#f05b41', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem',
            }}
          >
            Ir a Pedidos Directos
          </button>
        </div>
      ) : (
      <>
      <div className="mesas-header">
        <h2>Mapa de Mesas - Piso {pisoActual}</h2>
        {/* Ocultar selector de pisos si el usuario tiene piso asignado */}
        {(pisoRestringido === null || pisoRestringido === undefined) && (
        <div className="piso-selector">
          {pisosDisponibles.map(piso => (
            <button
              key={piso}
              className={pisoActual === piso ? 'active' : ''}
              onClick={() => setPisoActual(piso)}
            >
              Piso {piso}
            </button>
          ))}
        </div>
        )}
      </div>

      {mensajeActualizacion && (
        <div className="mensaje-actualizacion">
          {mensajeActualizacion}
        </div>
      )}
      
      {mesasFiltradas.length === 0 ? (
        <div className="no-mesas">No hay mesas disponibles en este piso</div>
      ) : (
        <div className="mesas-grid">
          {mesasFiltradas.map(mesa => (
            <div 
              key={mesa.id} 
              className={`mesa-item ${getColorEstado(mesa)} ${mesasConProductosListos.includes(mesa.id) ? 'parpadeo' : ''}`}
              onClick={() => handleMesaClick(mesa)}
            >
              <div className="mesa-numero">{mesa.numero}</div>
              <div className="mesa-capacidad">{mesa.capacidad} personas</div>
              <div className="mesa-estado">{mesa.estado.replace('_', ' ')}</div>
              {mesasConProductosListos.includes(mesa.id) && (
                <div className="indicador-productos-listos">
                  ¡Productos listos!
                </div>
              )}
              {mesa.id_mesero && (
                <div className="mesa-mesero">
                  Mesero: {mesa.mesero_nombre || mesa.id_mesero}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {mostrarDetalle && mesaSeleccionada && (
        <>
          <div className="modal-backdrop" onClick={() => setMostrarDetalle(false)}></div>
          <div className="detalle-mesa">
            <h3>Mesa {mesaSeleccionada.numero}</h3>
            <p>Estado: {mesaSeleccionada.estado.replace('_', ' ')}</p>
            <p>Capacidad: {mesaSeleccionada.capacidad} personas</p>
            
            {userInfo && (userInfo.rol === 'MOZO' || userInfo.rol === 'ENCARGADO') && (
              <div className="acciones-mesa">
                <h4>Cambiar Estado</h4>
                <div className="botones-estado">
                  <button 
                    className="btn-libre"
                    onClick={() => actualizarEstadoMesa(mesaSeleccionada.id, 'libre')}
                    disabled={mesaSeleccionada.estado === 'libre'}
                  >
                    Libre
                  </button>
                  <button 
                    className="btn-ocupada"
                    onClick={() => actualizarEstadoMesa(mesaSeleccionada.id, 'ocupada')}
                    disabled={mesaSeleccionada.estado === 'ocupada'}
                  >
                    Ocupada
                  </button>
                  <button 
                    className="btn-pendiente"
                    onClick={() => actualizarEstadoMesa(mesaSeleccionada.id, 'pendiente_pago')}
                    disabled={mesaSeleccionada.estado === 'pendiente_pago'}
                  >
                    Pendiente de Pago
                  </button>
                </div>

                <h4>Asignar Mesero</h4>
                {loadingMeseros ? (
                  <p>Cargando meseros...</p>
                ) : (
                  <select 
                    value={mesaSeleccionada.id_mesero || ''}
                    onChange={(e) => asignarMesero(mesaSeleccionada.id, e.target.value)}
                    className="select-mesero"
                  >
                    <option value="">Sin mesero asignado</option>
                    {meseros.map(mesero => (
                      <option key={mesero.id} value={mesero.id}>
                        {mesero.nombre} {mesero.apellido || ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {userInfo && (userInfo.rol === 'MOZO' || userInfo.rol === 'ENCARGADO') && (
              <div className="acciones-pedidos">
                <button 
                  className="btn-nuevo-pedido"
                  onClick={() => navigateToPedidos(mesaSeleccionada.id, 'nuevo')}
                  disabled={mesaSeleccionada.estado === 'libre'}
                >
                  Nuevo Pedido
                </button>

                <button 
                  className="btn-ver-pedidos"
                  onClick={() => navigateToPedidos(mesaSeleccionada.id, 'ver')}
                >
                  Ver Pedidos Activos
                </button>
              </div>
            )}

            <button 
              className="btn-cerrar"
              onClick={() => setMostrarDetalle(false)}
            >
              Cerrar
            </button>
          </div>
        </>
      )}
      </>
      )}

      {/* FAB - Acceso rápido a Pedido Directo */}
      <button
        className="fab-pedido-directo"
        title="Nuevo Pedido Directo"
        onClick={() => navigate('/pedido-directo')}
      >
        ⚡
      </button>
    </div>
  );
};

export default MesasModule;